import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { withdrawalApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { formatCurrency, formatDate } from '@/utils/format'
import { DollarSign, CheckCircle, XCircle, Clock, Info } from 'lucide-react'

// Withdrawal management page for admins
export default function Withdrawals() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('pending')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<any>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [detailsWithdrawal, setDetailsWithdrawal] = useState<any>(null)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [approveWithdrawal, setApproveWithdrawal] = useState<any>(null)
  const [adminNote, setAdminNote] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['withdrawals', statusFilter],
    queryFn: () => withdrawalApi.list(statusFilter),
  })

  const approveMutation = useMutation({
    mutationFn: ({ transaction_id, admin_note }: { transaction_id: number; admin_note?: string }) =>
      withdrawalApi.approve(transaction_id, admin_note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
      setApproveWithdrawal(null)
      setAdminNote('')
      setShowApproveModal(false)
      setSuccess('Withdrawal approved and notification sent to user!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to approve withdrawal')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ transaction_id, reason }: { transaction_id: number; reason?: string }) =>
      withdrawalApi.reject(transaction_id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
      setSelectedWithdrawal(null)
      setRejectReason('')
      setSuccess('Withdrawal rejected and coins refunded to user!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to reject withdrawal')
    },
  })

  const withdrawals = (data?.data as any)?.withdrawals || []

  const handleShowDetails = (withdrawal: any) => {
    setDetailsWithdrawal(withdrawal)
    setShowDetailsModal(true)
  }

  const handleApprove = (withdrawal: any) => {
    setApproveWithdrawal(withdrawal)
    setShowApproveModal(true)
  }

  const confirmApprove = () => {
    if (!approveWithdrawal) return
    approveMutation.mutate({
      transaction_id: approveWithdrawal.id,
      admin_note: adminNote || undefined,
    })
  }

  const handleReject = (withdrawal: any) => {
    setSelectedWithdrawal(withdrawal)
  }

  const confirmReject = () => {
    if (!selectedWithdrawal) return
    rejectMutation.mutate({
      transaction_id: selectedWithdrawal.id,
      reason: rejectReason || undefined,
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>
      case 'pending':
        return <Badge variant="warning">Pending</Badge>
      case 'rejected':
        return <Badge variant="error">Rejected</Badge>
      default:
        return <Badge variant="gray">{status}</Badge>
    }
  }

  const getSummary = () => {
    const total = withdrawals.length
    const totalAmount = withdrawals.reduce((sum: number, w: any) => sum + parseFloat(w.amount || 0), 0)
    const totalCoins = withdrawals.reduce((sum: number, w: any) => sum + parseFloat(w.coin_amount || 0), 0)
    return { total, totalAmount, totalCoins }
  }

  const summary = getSummary()

  if (isLoading) return <Loading />

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Withdrawal Requests</h1>
        <p className="text-gray-600 mt-1">Manage user withdrawal requests</p>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 mb-2">Total Requests</p>
              <p className="text-4xl font-bold">{summary.total}</p>
              <p className="text-blue-100 mt-1 text-sm">{statusFilter} status</p>
            </div>
            <Clock className="w-16 h-16 text-blue-300" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 mb-2">Total Coins</p>
              <p className="text-4xl font-bold">{summary.totalCoins.toFixed(2)}</p>
              <p className="text-yellow-100 mt-1 text-sm">Barter Coins</p>
            </div>
            <DollarSign className="w-16 h-16 text-yellow-300" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 mb-2">Total Amount</p>
              <p className="text-4xl font-bold">{formatCurrency(summary.totalAmount)}</p>
              <p className="text-green-100 mt-1 text-sm">USD payout</p>
            </div>
            <DollarSign className="w-16 h-16 text-green-300" />
          </div>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Card>
        <div className="flex space-x-4 border-b border-gray-200 pb-4">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              statusFilter === 'pending'
                ? 'bg-yellow-100 text-yellow-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              statusFilter === 'completed'
                ? 'bg-green-100 text-green-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              statusFilter === 'rejected'
                ? 'bg-red-100 text-red-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Rejected
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              statusFilter === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All
          </button>
        </div>
      </Card>

      {/* Withdrawals Table */}
      <Card title="Withdrawal Requests">
        {withdrawals.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No withdrawal requests</p>
            <p className="text-gray-400 text-sm mt-2">No {statusFilter} withdrawals found</p>
          </div>
        ) : (
          <ResponsiveTable>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coins</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {withdrawals.map((withdrawal: any) => (
                  <tr key={withdrawal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(withdrawal.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {withdrawal.first_name} {withdrawal.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{withdrawal.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-bold text-gray-900">
                        {parseFloat(withdrawal.coin_amount).toFixed(2)}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">coins</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-bold text-green-600">
                        {formatCurrency(withdrawal.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">
                        {parseFloat(withdrawal.barter_coin_balance).toFixed(2)} coins
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(withdrawal.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {withdrawal.status === 'pending' && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleShowDetails(withdrawal)}
                            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            title="View withdrawal details"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleApprove(withdrawal)}
                            className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            disabled={approveMutation.isPending}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(withdrawal)}
                            className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            disabled={rejectMutation.isPending}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </div>
                      )}
                      {withdrawal.status !== 'pending' && (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        )}
      </Card>

      {/* Withdrawal Details Modal */}
      {showDetailsModal && detailsWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Withdrawal Details</h3>

            <div className="space-y-4">
              <div className="border-b pb-3">
                <p className="text-sm text-gray-500">User</p>
                <p className="text-lg font-semibold text-gray-900">
                  {detailsWithdrawal.first_name} {detailsWithdrawal.last_name}
                </p>
                <p className="text-sm text-gray-600">{detailsWithdrawal.email}</p>
              </div>

              <div className="border-b pb-3">
                <p className="text-sm text-gray-500">Amount Requested</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(detailsWithdrawal.amount)}
                </p>
                <p className="text-sm text-gray-600">
                  {parseFloat(detailsWithdrawal.coin_amount).toFixed(2)} Barter Coins
                </p>
              </div>

              <div className="border-b pb-3">
                <p className="text-sm text-gray-500 mb-2">Withdrawal Method</p>
                <p className="text-lg font-semibold text-gray-900 capitalize mb-3">
                  {detailsWithdrawal.withdrawal_type || 'Not specified'}
                </p>

                {detailsWithdrawal.withdrawal_type === 'zelle' && detailsWithdrawal.zelle_email && (
                  <div className="bg-blue-50 p-3 rounded-md">
                    <p className="text-sm text-gray-600">Zelle Email</p>
                    <p className="font-medium text-gray-900">{detailsWithdrawal.zelle_email}</p>
                  </div>
                )}

                {detailsWithdrawal.withdrawal_type === 'paypal' && detailsWithdrawal.paypal_email && (
                  <div className="bg-blue-50 p-3 rounded-md">
                    <p className="text-sm text-gray-600">PayPal Email</p>
                    <p className="font-medium text-gray-900">{detailsWithdrawal.paypal_email}</p>
                  </div>
                )}

                {detailsWithdrawal.withdrawal_type === 'ach' && (
                  <div className="bg-blue-50 p-3 rounded-md space-y-2">
                    <div>
                      <p className="text-sm text-gray-600">Bank Name</p>
                      <p className="font-medium text-gray-900">{detailsWithdrawal.ach_bank_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Holder</p>
                      <p className="font-medium text-gray-900">{detailsWithdrawal.ach_account_holder || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Number</p>
                      <p className="font-medium text-gray-900">{detailsWithdrawal.ach_account_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Routing Number</p>
                      <p className="font-medium text-gray-900">{detailsWithdrawal.ach_routing_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Type</p>
                      <p className="font-medium text-gray-900 capitalize">{detailsWithdrawal.ach_account_type || 'N/A'}</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-500">Current Balance</p>
                <p className="text-lg font-semibold text-gray-900">
                  {parseFloat(detailsWithdrawal.barter_coin_balance).toFixed(2)} coins
                </p>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDetailsModal(false)
                  setDetailsWithdrawal(null)
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && approveWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Approve Withdrawal</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to approve this withdrawal request for{' '}
              <strong>{formatCurrency(approveWithdrawal.amount)}</strong> from{' '}
              <strong>
                {approveWithdrawal.first_name} {approveWithdrawal.last_name}
              </strong>
              ? An email notification will be sent to the user.
            </p>
            <div className="mb-4">
              <label className="label">Admin Note (optional)</label>
              <textarea
                className="input"
                rows={3}
                placeholder="Add a note for the user (will be included in the email notification)..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
              />
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowApproveModal(false)
                  setApproveWithdrawal(null)
                  setAdminNote('')
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmApprove}
                className="flex-1 bg-green-600 hover:bg-green-700"
                isLoading={approveMutation.isPending}
              >
                Approve & Notify
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {selectedWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Withdrawal</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to reject this withdrawal request from{' '}
              <strong>
                {selectedWithdrawal.first_name} {selectedWithdrawal.last_name}
              </strong>
              ? The coins will be refunded to their account and an email notification will be sent.
            </p>
            <div className="mb-4">
              <label className="label">Reason (optional)</label>
              <textarea
                className="input"
                rows={3}
                placeholder="Enter reason for rejection (will be included in the email notification)..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectedWithdrawal(null)
                  setRejectReason('')
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmReject}
                className="flex-1 bg-red-600 hover:bg-red-700"
                isLoading={rejectMutation.isPending}
              >
                Reject & Refund
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
